import { useFormik } from "formik";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import * as Yup from "yup";
import { STATUS_DROPDOWN } from "../../../developmentContent/dropdown";
import { mergeClass } from "../../../resources/utils/helper";
import Button from "../../atoms/Button/index";
import { Input } from "../../atoms/Input/Input";
import ModalSkeleton from "../../atoms/ModalSkeleton/index";
import DropDown from "../../molecules/DropDown/DropDown";
import UploadImageBoxNew from "../../molecules/UploadImageBoxNew/UploadImageBoxNew/index";
import QuillInput from "../../organisms/QuillInput";
import classes from "./LocationModal.module.css";

const LocationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  county: Yup.string().required("County is required"),
  city: Yup.string(),
  description: Yup.string(),
  slug: Yup.string(),
  metaTitle: Yup.string(),
  metaDescription: Yup.string(),
  metaKeywords: Yup.string(),
});

export const LocationModal = ({
  showModal = false,
  setShowModal = () => {},
  loading = false,
  data,
  handleSubmit = () => {},
}) => {
  const formik = useFormik({
    initialValues: {
      title: "",
      subTitle: "",
      county: "",
      city: "",
      slug: "",
      description: "",
      metaTitle: "",
      metaDescription: "",
      metaKeywords: "",
      status: { label: "Activate", value: true },
      image: null,
    },
    validationSchema: LocationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  useEffect(() => {
    if (data) {
      formik.setValues({
        title: data?.title || "",
        subTitle: data?.subTitle || "",
        county: data?.county || "",
        city: data?.city || "",
        slug: data?.slug || "",
        image: data?.image || null,
        description: data?.description || "",
        metaTitle: data?.metaTitle || "",
        metaDescription: data?.metaDescription || "",
        metaKeywords: data?.metaKeywords || "",
        status: {
          label: data?.isActive ? "Activate" : "Deactivate",
          value: data?.isActive ? true : false,
        },
      });
    }
  }, [data]);

  const handleCountyCityChange = (field, val) => {
    formik.setFieldValue(field, val);
    if (!data?.slug) {
      const countyVal = field === "county" ? val : formik.values.county;
      const cityVal = field === "city" ? val : formik.values.city;
      const cleanCounty = (countyVal || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-");
      const cleanCity = (cityVal || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-");
      if (cleanCity) {
        formik.setFieldValue("slug", cleanCity);
      } else if (cleanCounty) {
        formik.setFieldValue("slug", cleanCounty);
      }
    }
  };

  return (
    <ModalSkeleton
      size="xl"
      show={showModal}
      setShow={setShowModal}
      modalStyles={classes.modal}
      header={`${data ? "Edit Location Page (SEO)" : "Add Location Page (SEO)"}`}
      headerClass={classes.header}
      showCloseIcon
      headerHeading={classes.heading}
    >
      <div className={classes.mainDiv}>
        <form onSubmit={formik.handleSubmit}>
          <Row className="g-4">
            {/* Left Column: Metadata, Geography, SEO */}
            <Col lg={5} md={12} className={classes.leftColumn}>
              <Input
                label={"Page Title"}
                value={formik.values.title}
                setValue={(val) => formik.setFieldValue("title", val)}
                errorText={formik.touched.title && formik.errors.title}
                placeholder={"e.g. Fresh Produce Delivery in West Palm Beach"}
              />

              <Input
                label={"Banner Subtitle / Tagline"}
                value={formik.values.subTitle}
                setValue={(val) => formik.setFieldValue("subTitle", val)}
                placeholder={"e.g. Serving premier restaurants & hotels throughout Palm Beach"}
              />

              <Row className="g-2">
                <Col sm={6}>
                  <Input
                    label={"County"}
                    value={formik.values.county}
                    setValue={(val) => handleCountyCityChange("county", val)}
                    errorText={formik.touched.county && formik.errors.county}
                    placeholder={"Palm Beach County"}
                  />
                </Col>
                <Col sm={6}>
                  <Input
                    label={"City"}
                    value={formik.values.city}
                    setValue={(val) => handleCountyCityChange("city", val)}
                    errorText={formik.touched.city && formik.errors.city}
                    placeholder={"West Palm Beach"}
                  />
                </Col>
              </Row>

              <Input
                label={"SEO Slug (URL Path)"}
                value={formik.values.slug}
                setValue={(val) => formik.setFieldValue("slug", val)}
                errorText={formik.touched.slug && formik.errors.slug}
                placeholder={"palm-beach-county/west-palm-beach"}
              />

              <div className={classes.uploadRow}>
                <div>
                  <UploadImageBoxNew
                    containerClass={classes.uploadImageContainerClass}
                    hideDeleteIcon={true}
                    state={formik.values.image}
                    setter={(value) => formik.setFieldValue("image", value)}
                    onDelete={() => formik.setFieldValue("image", null)}
                    imgClass={classes.uploadImage}
                    label={"Featured Image"}
                    errorText={formik.touched.image && formik.errors.image}
                  />
                </div>
                <div className="flex-grow-1">
                  <DropDown
                    className={mergeClass(classes.dropDownContainer, classes.customContainerClass)}
                    options={STATUS_DROPDOWN?.filter((e) => e?.value !== "all")}
                    setValue={(val) => formik.setFieldValue("status", val)}
                    value={formik.values.status}
                    label={"Page Status"}
                    placeholder={"Select Status"}
                    labelClassName={classes.myLabel}
                    errorText={formik.touched.status && formik.errors.status}
                  />
                </div>
              </div>

              {/* SEO Meta Fields */}
              <div className={classes.sectionDivider}>SEO Search Optimization</div>

              <Input
                label={"Meta Title Tag"}
                value={formik.values.metaTitle}
                setValue={(val) => formik.setFieldValue("metaTitle", val)}
                placeholder={"Title appearing on Google search results"}
              />

              <Input
                label={"Meta Keywords"}
                value={formik.values.metaKeywords}
                setValue={(val) => formik.setFieldValue("metaKeywords", val)}
                placeholder={"produce, west palm beach, wholesale, delivery"}
              />

              <Input
                label={"Meta Description Tag"}
                value={formik.values.metaDescription}
                setValue={(val) => formik.setFieldValue("metaDescription", val)}
                placeholder={"Brief summary for Google snippets"}
              />
            </Col>

            {/* Right Column: Visual Content (VC) Editor */}
            <Col lg={7} md={12} className={classes.rightColumn}>
              <QuillInput
                label={"Location Page Content (Visual / Rich Text Editor)"}
                setValue={(val) => formik.setFieldValue("description", val)}
                value={formik.values.description}
                placeholder={"Write and format rich body content, headings, lists, and information for this location page..."}
                errorText={formik.touched.description && formik.errors.description}
              />
            </Col>

            {/* Bottom Footer Actions */}
            <Col xs={12}>
              <div className={classes.buttonDiv}>
                <Button
                  variant="primary"
                  label={loading ? "Saving..." : data?.slug ? "Update Location" : "Save Location"}
                  onClick={formik.handleSubmit}
                  type={"submit"}
                  disabled={loading}
                />
                <Button
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </div>
    </ModalSkeleton>
  );
};

export default LocationModal;
